import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faArrowRight, faSpinner, faSync, faCheck, faTimes,
    faUndo, faHandPointer, faFileInvoice, faSearch,
    faExclamationTriangle, faCheckCircle, faBan,
    faClipboardList, faBalanceScale, faFlagCheckered, faBolt,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import logo from '../../assets/images/IMT_logo.png';
import bankReconService from '../../services/bankReconService';
import ManualMatchModal from '../../components/BankReconciliation/ManualMatchModal';
import RecordLoadProblem, { describeLoadFailure } from '../../components/ui/RecordLoadProblem';
import '../../pages/Invoicing/OutgoingInvoice.css';
import '../../components/ui/HMSHeaderStyles.css';
import './ReconciliationScreen.css';

const DOC_TYPE_LABELS = {
    outgoing_invoice: 'Outgoing Invoice',
    incoming_invoice: 'Incoming Invoice',
    credit_note: 'Credit Note',
    debit_note: 'Debit Note',
    proforma: 'Pro-Forma',
};

const STEP_NAMES = ['Overview', 'Review & Match', 'Summary'];
const STEP_ICONS = [faClipboardList, faBalanceScale, faFlagCheckered];

const ReconciliationScreen = () => {
    const { statementId } = useParams();
    const history = useHistory();

    const [currentStep, setCurrentStep] = useState(1);
    const [statement, setStatement] = useState(null);
    const [lines, setLines] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selectedLine, setSelectedLine] = useState(null);
    const [lineMatches, setLineMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [matchRunning, setMatchRunning] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [showManualMatch, setShowManualMatch] = useState(false);
    const [actionError, setActionError] = useState('');
    const [approveResult, setApproveResult] = useState(null);
    // Row 125: why the statement in the address didn't load. While set, the
    // screen shows RecordLoadProblem instead of a silently empty statement.
    const [loadProblem, setLoadProblem] = useState(null);
    const [loadAttempt, setLoadAttempt] = useState(0);

    const loadData = useCallback(async () => {
        setLoadProblem(null);
        try {
            const [stmtRes, linesRes, summaryRes] = await Promise.all([
                bankReconService.getStatements({}),
                bankReconService.getReconciliationLines(statementId),
                bankReconService.getReconciliationSummary(statementId),
            ]);
            const stmts = stmtRes.data || [];
            const found = stmts.find(s => s.id === statementId);
            setStatement(found || null);
            setLines(linesRes.data || []);
            setSummary(summaryRes.data || null);
        } catch (err) {
            console.error('Failed to load reconciliation data:', err);
            setLoadProblem(describeLoadFailure(err));
        } finally {
            setLoading(false);
        }
    }, [statementId]);

    useEffect(() => {
        loadData();
    }, [loadData, loadAttempt]);

    useEffect(() => {
        if (selectedLine) {
            bankReconService.getLineMatches(statementId, selectedLine.id)
                .then(res => setLineMatches(res.data || []))
                .catch(() => setLineMatches([]));
        } else {
            setLineMatches([]);
        }
    }, [selectedLine, statementId]);

    const refreshLineContext = async (lineId) => {
        if (!lineId) return;
        try {
            const res = await bankReconService.getLineMatches(statementId, lineId);
            setLineMatches(res.data || []);
        } catch { setLineMatches([]); }
    };

    const handleRunMatching = async () => {
        setMatchRunning(true);
        setActionError('');
        try {
            await bankReconService.runMatching(statementId);
            await loadData();
            if (selectedLine) await refreshLineContext(selectedLine.id);
        } catch (err) {
            const msg = err.response?.data?.error || 'Matching failed. Please try again.';
            setActionError(msg);
        } finally {
            setMatchRunning(false);
        }
    };

    const handleApproveAutoMatches = async () => {
        setActionLoading('approve-auto');
        setActionError('');
        setApproveResult(null);
        try {
            const res = await bankReconService.approveAutoMatches(statementId);
            setApproveResult(res.data || null);
            await loadData();
            if (selectedLine) await refreshLineContext(selectedLine.id);
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to approve auto-matches.';
            setActionError(msg);
        } finally {
            setActionLoading(null);
        }
    };

    const handleConfirmMatch = async (matchId) => {
        if (!selectedLine) return;
        setActionLoading(matchId);
        setActionError('');
        try {
            await bankReconService.confirmMatch(statementId, selectedLine.id, matchId);
            await loadData();
            await refreshLineContext(selectedLine.id);
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to confirm match.';
            setActionError(msg);
        } finally {
            setActionLoading(null);
        }
    };

    const handleRejectMatch = async (matchId) => {
        if (!selectedLine) return;
        setActionLoading(matchId);
        setActionError('');
        try {
            await bankReconService.rejectMatch(statementId, selectedLine.id, matchId);
            await refreshLineContext(selectedLine.id);
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to reject match.';
            setActionError(msg);
        } finally {
            setActionLoading(null);
        }
    };

    const handleUnmatch = async () => {
        if (!selectedLine) return;
        setActionLoading('unmatch');
        setActionError('');
        try {
            await bankReconService.unmatchLine(statementId, selectedLine.id);
            await loadData();
            await refreshLineContext(selectedLine.id);
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to unmatch.';
            setActionError(msg);
        } finally {
            setActionLoading(null);
        }
    };

    const handleManualMatchComplete = async () => {
        setShowManualMatch(false);
        setActionError('');
        await loadData();
        if (selectedLine) await refreshLineContext(selectedLine.id);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
            });
        } catch { return dateStr; }
    };

    const formatAmount = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return '-';
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getStatusClass = (status) => {
        if (status === 'matched' || status === 'manually_matched') return 'recon-status-matched';
        return 'recon-status-unmatched';
    };

    const getStatusLabel = (status) => {
        if (status === 'matched') return 'Matched';
        if (status === 'manually_matched') return 'Manual';
        return 'Unmatched';
    };

    useEffect(() => {
        if (selectedLine && lines.length > 0) {
            const updated = lines.find(l => l.id === selectedLine.id);
            if (updated) {
                setSelectedLine(updated);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines]);

    const goToStep = (step) => {
        setCurrentStep(step);
        setActionError('');
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
    };

    const reconPercent = summary ? summary.reconciled_percent : 0;
    // Comes from the API, not re-derived here: the button's number and the
    // endpoint's eligibility rule must be the same rule.
    const autoMatchLines = summary ? (summary.auto_match_lines || 0) : 0;
    const matchedCount = summary ? (summary.matched + summary.manually_matched) : 0;
    const totalLines = summary ? summary.total_lines : 0;

    if (loading) {
        return (
            <Layout showLeftNavSidebar={true} showRightSidebar={false}>
                <div className="recon-screen">
                    <div className="recon-loading-wrapper">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p>Loading reconciliation data...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (loadProblem) {
        return (
            <Layout showLeftNavSidebar={true} showRightSidebar={false}>
                <div className="recon-screen">
                    <RecordLoadProblem
                        problem={loadProblem}
                        recordName="bank statement"
                        areaName="bank reconciliation"
                        backLabel="Back to Bank Reconciliation"
                        onBack={() => history.push('/invoicing/bank-reconciliation')}
                        onRetry={() => {
                            setLoading(true);
                            setLoadAttempt((n) => n + 1);
                        }}
                    />
                </div>
            </Layout>
        );
    }

    return (
        <Layout showLeftNavSidebar={true} showRightSidebar={false}>
            <div className="recon-screen">
                {/* Standard Header */}
                <div className="hms-screen-header recon-hms-header">
                    <button
                        className="recon-back-button"
                        onClick={() => history.push('/invoicing/bank-reconciliation')}
                        title="Back to Bank Reconciliation"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>

                    <div className="header-logo">
                        <img src={logo} alt="IMT" className="header-logo-img" />
                        <div className="header-title-content">
                            <h1 className="hms-screen-title">RECONCILIATION</h1>
                            {statement && (
                                <p className="recon-header-subtitle">
                                    {statement.bank_name} | {statement.iban} | {formatDate(statement.statement_date)} | {statement.currency}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="recon-header-actions">
                        <button
                            className="recon-header-action-btn secondary"
                            onClick={loadData}
                            disabled={matchRunning}
                        >
                            <FontAwesomeIcon icon={faSync} /> Refresh
                        </button>
                    </div>
                </div>

                <div className="recon-form-container">
                    {/* Step Progress */}
                    <div className="recon-progress-stepper">
                        {STEP_NAMES.map((name, idx) => (
                            <React.Fragment key={idx}>
                                <div
                                    className={`recon-progress-step ${currentStep > idx + 1 ? 'completed' : ''} ${currentStep === idx + 1 ? 'active' : ''}`}
                                    onClick={() => goToStep(idx + 1)}
                                >
                                    <div className="recon-progress-number">
                                        {currentStep > idx + 1
                                            ? <FontAwesomeIcon icon={faCheck} />
                                            : <FontAwesomeIcon icon={STEP_ICONS[idx]} />
                                        }
                                    </div>
                                    <span className="recon-progress-label">{name}</span>
                                </div>
                                {idx < STEP_NAMES.length - 1 && (
                                    <div className={`recon-progress-connector ${currentStep > idx + 1 ? 'completed' : ''}`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {actionError && (
                        <div className="recon-error-banner">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>{actionError}</span>
                            <button className="recon-error-dismiss" onClick={() => setActionError('')}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    )}

                    {/* STEP 1: Overview & Auto-Match */}
                    {currentStep === 1 && (
                        <div className="recon-step">
                            <h2 className="recon-step-title">
                                <FontAwesomeIcon icon={faClipboardList} className="recon-step-icon" />
                                Statement Overview
                            </h2>
                            <p className="recon-step-desc">
                                Review the statement details and run the auto-matching engine to find potential matches against your invoices and documents.
                            </p>

                            {/* Statement Info Card */}
                            {statement && (
                                <div className="recon-info-card">
                                    <div className="recon-info-grid">
                                        <div className="recon-info-item">
                                            <span className="recon-info-label">Bank</span>
                                            <span className="recon-info-value">{statement.bank_name}</span>
                                        </div>
                                        <div className="recon-info-item">
                                            <span className="recon-info-label">IBAN</span>
                                            <span className="recon-info-value">{statement.iban}</span>
                                        </div>
                                        <div className="recon-info-item">
                                            <span className="recon-info-label">Statement Date</span>
                                            <span className="recon-info-value">{formatDate(statement.statement_date)}</span>
                                        </div>
                                        <div className="recon-info-item">
                                            <span className="recon-info-label">Currency</span>
                                            <span className="recon-info-value">{statement.currency}</span>
                                        </div>
                                        <div className="recon-info-item">
                                            <span className="recon-info-label">Entity</span>
                                            <span className="recon-info-value">{statement.hms_entity_name || '-'}</span>
                                        </div>
                                        <div className="recon-info-item">
                                            <span className="recon-info-label">File</span>
                                            <span className="recon-info-value">{statement.original_filename}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Progress Summary Card */}
                            {summary && (
                                <div className="recon-info-card">
                                    <div className="recon-progress-bar-section">
                                        <div className="recon-progress-bar-header">
                                            <span className="recon-progress-bar-title">Reconciliation Progress</span>
                                            <span className="recon-progress-bar-percent">{reconPercent}%</span>
                                        </div>
                                        <div className="recon-progress-bar-track">
                                            <div
                                                className="recon-progress-bar-fill"
                                                style={{ width: `${reconPercent}%` }}
                                            />
                                        </div>
                                        <div className="recon-progress-stats">
                                            <div className="recon-stat-card stat-matched">
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                <div>
                                                    <span className="recon-stat-number">{matchedCount}</span>
                                                    <span className="recon-stat-label">Matched</span>
                                                </div>
                                            </div>
                                            <div className="recon-stat-card stat-suggestions">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                <div>
                                                    <span className="recon-stat-number">{summary.with_suggestions}</span>
                                                    <span className="recon-stat-label">Suggestions</span>
                                                </div>
                                            </div>
                                            <div className="recon-stat-card stat-unmatched">
                                                <FontAwesomeIcon icon={faBan} />
                                                <div>
                                                    <span className="recon-stat-number">{summary.unmatched - summary.with_suggestions}</span>
                                                    <span className="recon-stat-label">No Match</span>
                                                </div>
                                            </div>
                                            <div className="recon-stat-card stat-total">
                                                <FontAwesomeIcon icon={faClipboardList} />
                                                <div>
                                                    <span className="recon-stat-number">{totalLines}</span>
                                                    <span className="recon-stat-label">Total Lines</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Auto-Match Action */}
                            <div className="recon-action-card">
                                <div className="recon-action-card-content">
                                    <div className="recon-action-text">
                                        <h3>Run Auto-Matching Engine</h3>
                                        <p>The matching engine will compare each bank statement line against your invoices, credit notes, and debit notes to find potential matches based on amount, partner, and reference data.</p>
                                    </div>
                                    <button
                                        className="recon-action-btn primary"
                                        onClick={handleRunMatching}
                                        disabled={matchRunning}
                                    >
                                        {matchRunning
                                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Running...</>
                                            : <><FontAwesomeIcon icon={faSearch} /> Run Matching</>
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: Review & Match */}
                    {currentStep === 2 && (
                        <div className="recon-step">
                            <h2 className="recon-step-title">
                                <FontAwesomeIcon icon={faBalanceScale} className="recon-step-icon" />
                                Review &amp; Confirm Matches
                            </h2>
                            <p className="recon-step-desc">
                                Click a bank statement line on the left to view match suggestions on the right. Confirm good matches, reject incorrect ones, or match manually.
                            </p>

                            {/* Compact progress bar */}
                            {summary && (
                                <div className="recon-compact-progress">
                                    <div className="recon-compact-progress-track">
                                        <div className="recon-progress-bar-fill" style={{ width: `${reconPercent}%` }} />
                                    </div>
                                    <div className="recon-compact-progress-stats">
                                        <span><strong>{reconPercent}%</strong> reconciled</span>
                                        <span><FontAwesomeIcon icon={faCheckCircle} className="stat-icon-green" /> {matchedCount} matched</span>
                                        {autoMatchLines > 0 && (
                                            <span><FontAwesomeIcon icon={faBolt} className="stat-icon-green" /> {autoMatchLines} auto-matched</span>
                                        )}
                                        <span><FontAwesomeIcon icon={faExclamationTriangle} className="stat-icon-amber" /> {summary.with_suggestions} suggestions</span>
                                        <span><FontAwesomeIcon icon={faBan} className="stat-icon-red" /> {summary.unmatched - summary.with_suggestions} no match</span>
                                        <span className="stat-total">{totalLines} total</span>
                                    </div>
                                </div>
                            )}

                            {/* One-click approval for the unambiguous matches.
                                Auto-match proposes; a human still approves. */}
                            {autoMatchLines > 0 && (
                                <div className="recon-auto-approve-bar">
                                    <div className="recon-auto-approve-text">
                                        <FontAwesomeIcon icon={faBolt} className="recon-auto-approve-icon" />
                                        <span>
                                            <strong>{autoMatchLines}</strong>
                                            {autoMatchLines === 1 ? ' line has' : ' lines have'} an exact amount and partner match
                                            waiting for your approval.
                                        </span>
                                    </div>
                                    <button
                                        className="recon-btn recon-btn-confirm"
                                        onClick={handleApproveAutoMatches}
                                        disabled={actionLoading === 'approve-auto'}
                                    >
                                        {actionLoading === 'approve-auto'
                                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Approving...</>
                                            : <><FontAwesomeIcon icon={faCheck} /> Approve all auto-matches ({autoMatchLines})</>
                                        }
                                    </button>
                                </div>
                            )}

                            {/* Nothing is ever silently skipped -- a line the bulk
                                action could not take is named with its reason. */}
                            {approveResult && (
                                <div className="recon-auto-approve-result">
                                    <div className="recon-auto-approve-result-head">
                                        <FontAwesomeIcon icon={faCheckCircle} className="stat-icon-green" />
                                        {approveResult.approved_count} approved
                                        {approveResult.skipped_count > 0 && `, ${approveResult.skipped_count} skipped`}
                                        <button
                                            className="recon-auto-approve-dismiss"
                                            onClick={() => setApproveResult(null)}
                                            title="Dismiss"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                    {(approveResult.skipped || []).map((sk, i) => (
                                        <div key={i} className="recon-auto-approve-skip">
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="stat-icon-amber" />
                                            Line #{sk.line_number}: {sk.reason}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Split View */}
                            <div className="recon-split">
                                {/* Left: Bank lines */}
                                <div className="recon-lines-panel">
                                    <div className="recon-panel-header">Bank Statement Lines</div>
                                    <div className="recon-lines-scroll">
                                        {lines.map(line => {
                                            const isSelected = selectedLine?.id === line.id;
                                            const isCredit = line.credit_debit === 'CREDIT';
                                            return (
                                                <div
                                                    key={line.id}
                                                    className={`recon-line-card ${isSelected ? 'selected' : ''} ${getStatusClass(line.reconciliation_status)}`}
                                                    onClick={() => setSelectedLine(line)}
                                                >
                                                    <div className="recon-line-top">
                                                        <span className="recon-line-num">#{line.line_number}</span>
                                                        <span className="recon-line-date">{formatDate(line.transaction_date)}</span>
                                                        <span className={`recon-line-amount ${isCredit ? 'credit' : 'debit'}`}>
                                                            {isCredit ? '+' : '-'}{formatAmount(line.amount)}
                                                        </span>
                                                    </div>
                                                    <div className="recon-line-mid">
                                                        <span className="recon-line-partner">{line.partner_name || '-'}</span>
                                                    </div>
                                                    <div className="recon-line-bottom">
                                                        <span className="recon-line-desc">{line.description || line.reference || '-'}</span>
                                                        <span className={`recon-line-status ${getStatusClass(line.reconciliation_status)}`}>
                                                            {getStatusLabel(line.reconciliation_status)}
                                                            {line.match_count > 0 && line.reconciliation_status === 'unmatched' && (
                                                                <span className={`recon-line-suggestion-badge ${line.auto_match_count > 0 ? 'is-auto' : ''}`}>
                                                                    {line.auto_match_count > 0
                                                                        ? <FontAwesomeIcon icon={faBolt} />
                                                                        : line.match_count}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {lines.length === 0 && (
                                            <div className="recon-empty">No transaction lines found.</div>
                                        )}
                                    </div>
                                </div>

                                {/* Right: Match details */}
                                <div className="recon-detail-panel">
                                    {!selectedLine ? (
                                        <div className="recon-detail-empty">
                                            <FontAwesomeIcon icon={faHandPointer} className="recon-detail-empty-icon" />
                                            <p>Select a bank line to view match details</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="recon-panel-header">
                                                Match Details -- Line #{selectedLine.line_number}
                                            </div>

                                            <div className="recon-selected-summary">
                                                <div className="recon-selected-row">
                                                    <span className="recon-selected-label">Date</span>
                                                    <span>{formatDate(selectedLine.transaction_date)}</span>
                                                </div>
                                                <div className="recon-selected-row">
                                                    <span className="recon-selected-label">Amount</span>
                                                    <span className={selectedLine.credit_debit === 'CREDIT' ? 'recon-amount-credit' : 'recon-amount-debit'}>
                                                        {selectedLine.credit_debit === 'CREDIT' ? '+' : '-'}{formatAmount(selectedLine.amount)} {selectedLine.currency}
                                                    </span>
                                                </div>
                                                <div className="recon-selected-row">
                                                    <span className="recon-selected-label">Partner</span>
                                                    <span>{selectedLine.partner_name || '-'}</span>
                                                </div>
                                                <div className="recon-selected-row">
                                                    <span className="recon-selected-label">Description</span>
                                                    <span className="recon-selected-desc">{selectedLine.description || '-'}</span>
                                                </div>
                                                {selectedLine.reference && (
                                                    <div className="recon-selected-row">
                                                        <span className="recon-selected-label">Reference</span>
                                                        <span>{selectedLine.reference}</span>
                                                    </div>
                                                )}
                                                <div className="recon-selected-row">
                                                    <span className="recon-selected-label">Status</span>
                                                    <span className={`recon-line-status ${getStatusClass(selectedLine.reconciliation_status)}`}>
                                                        {getStatusLabel(selectedLine.reconciliation_status)}
                                                    </span>
                                                </div>
                                            </div>

                                            {(selectedLine.reconciliation_status === 'matched' || selectedLine.reconciliation_status === 'manually_matched') && (
                                                <div className="recon-matched-actions">
                                                    <button
                                                        className="recon-btn recon-btn-warning"
                                                        onClick={handleUnmatch}
                                                        disabled={actionLoading === 'unmatch'}
                                                    >
                                                        {actionLoading === 'unmatch'
                                                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Unmatching...</>
                                                            : <><FontAwesomeIcon icon={faUndo} /> Unmatch</>
                                                        }
                                                    </button>
                                                </div>
                                            )}

                                            {selectedLine.reconciliation_status === 'unmatched' && (
                                                <>
                                                    <div className="recon-matches-header">
                                                        Suggested Matches ({lineMatches.length})
                                                    </div>
                                                    {lineMatches.length === 0 ? (
                                                        <div className="recon-no-matches">
                                                            No matching documents found. Try running matching or match manually.
                                                        </div>
                                                    ) : (
                                                        <div className="recon-matches-list">
                                                            {lineMatches.map(match => (
                                                                <div key={match.id} className={`recon-match-card ${match.is_auto_match ? 'high-confidence' : ''}`}>
                                                                    <div className="recon-match-header">
                                                                        <div className="recon-match-doc">
                                                                            <FontAwesomeIcon icon={faFileInvoice} className="recon-match-doc-icon" />
                                                                            <div>
                                                                                <span className="recon-match-type">{DOC_TYPE_LABELS[match.document_type] || match.document_type}</span>
                                                                                <span className="recon-match-number">#{match.document_number}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="recon-match-score">
                                                                            {match.is_auto_match ? (
                                                                                /* The tag IS the verdict. Showing "60%" beside it
                                                                                   reads as a hedge on a flawless match -- the same
                                                                                   trust problem the amber badge caused. The score
                                                                                   stays visible on the tiers where it informs. */
                                                                                <span className="recon-auto-match-tag">
                                                                                    <FontAwesomeIcon icon={faBolt} /> Auto-matched
                                                                                </span>
                                                                            ) : (
                                                                                <span className={`recon-score-badge ${match.score >= 50 ? 'medium' : 'low'}`}>
                                                                                    {match.score}%
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="recon-match-details">
                                                                        <div className="recon-match-row">
                                                                            <span>Partner:</span>
                                                                            <span>{match.partner_name || '-'}</span>
                                                                        </div>
                                                                        <div className="recon-match-row">
                                                                            <span>Doc Amount:</span>
                                                                            <span>{formatAmount(match.document_amount)} {match.currency}</span>
                                                                        </div>
                                                                        {parseFloat(match.amount_difference) !== 0 && (
                                                                            <div className="recon-match-row recon-match-diff">
                                                                                <span>Difference:</span>
                                                                                <span>{formatAmount(match.amount_difference)}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {match.match_reasons && match.match_reasons.length > 0 && (
                                                                        <div className="recon-match-reasons">
                                                                            {match.match_reasons.map((r, i) => (
                                                                                <span key={i} className="recon-reason-tag good">{r}</span>
                                                                            ))}
                                                                            {(match.mismatch_reasons || []).map((r, i) => (
                                                                                <span key={`m${i}`} className="recon-reason-tag bad">{r}</span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    <div className="recon-match-actions">
                                                                        <button
                                                                            className="recon-btn recon-btn-confirm"
                                                                            onClick={() => handleConfirmMatch(match.id)}
                                                                            disabled={actionLoading === match.id}
                                                                        >
                                                                            {actionLoading === match.id
                                                                                ? <FontAwesomeIcon icon={faSpinner} spin />
                                                                                : <><FontAwesomeIcon icon={faCheck} /> Confirm</>
                                                                            }
                                                                        </button>
                                                                        <button
                                                                            className="recon-btn recon-btn-reject"
                                                                            onClick={() => handleRejectMatch(match.id)}
                                                                            disabled={actionLoading === match.id}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTimes} /> Reject
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="recon-manual-section">
                                                        <button
                                                            className="recon-btn recon-btn-manual"
                                                            onClick={() => setShowManualMatch(true)}
                                                        >
                                                            <FontAwesomeIcon icon={faHandPointer} /> Match Manually
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            {(selectedLine.reconciliation_status === 'matched' || selectedLine.reconciliation_status === 'manually_matched') && lineMatches.length > 0 && (
                                                <div className="recon-confirmed-match">
                                                    <div className="recon-matches-header">Confirmed Match</div>
                                                    {lineMatches.filter(m => m.is_confirmed).map(match => (
                                                        <div key={match.id} className="recon-match-card confirmed">
                                                            <div className="recon-match-header">
                                                                <div className="recon-match-doc">
                                                                    <FontAwesomeIcon icon={faFileInvoice} className="recon-match-doc-icon" />
                                                                    <div>
                                                                        <span className="recon-match-type">{DOC_TYPE_LABELS[match.document_type] || match.document_type}</span>
                                                                        <span className="recon-match-number">#{match.document_number || 'N/A'}</span>
                                                                    </div>
                                                                </div>
                                                                <FontAwesomeIcon icon={faCheckCircle} className="recon-confirmed-icon" />
                                                            </div>
                                                            <div className="recon-match-details">
                                                                <div className="recon-match-row">
                                                                    <span>Partner:</span>
                                                                    <span>{match.partner_name || '-'}</span>
                                                                </div>
                                                                <div className="recon-match-row">
                                                                    <span>Doc Amount:</span>
                                                                    <span>{formatAmount(match.document_amount)} {match.currency}</span>
                                                                </div>
                                                                {parseFloat(match.amount_difference) !== 0 && (
                                                                    <div className="recon-match-row recon-match-diff">
                                                                        <span>Difference:</span>
                                                                        <span>{formatAmount(match.amount_difference)}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {match.match_reasons && match.match_reasons.length > 0 && (
                                                                <div className="recon-match-reasons">
                                                                    {match.match_reasons.map((r, i) => (
                                                                        <span key={i} className="recon-reason-tag good">{r}</span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 3: Summary */}
                    {currentStep === 3 && (
                        <div className="recon-step">
                            <h2 className="recon-step-title">
                                <FontAwesomeIcon icon={faFlagCheckered} className="recon-step-icon" />
                                Reconciliation Summary
                            </h2>
                            <p className="recon-step-desc">
                                Overview of the reconciliation status for this bank statement.
                            </p>

                            {summary && (
                                <div className="recon-summary-card">
                                    <div className="recon-summary-percent-ring">
                                        <svg viewBox="0 0 120 120" className="recon-ring-svg">
                                            <circle cx="60" cy="60" r="52" className="recon-ring-bg" />
                                            <circle
                                                cx="60" cy="60" r="52"
                                                className="recon-ring-fill"
                                                strokeDasharray={`${(reconPercent / 100) * 326.73} 326.73`}
                                            />
                                        </svg>
                                        <div className="recon-ring-text">
                                            <span className="recon-ring-number">{reconPercent}%</span>
                                            <span className="recon-ring-label">Reconciled</span>
                                        </div>
                                    </div>

                                    <div className="recon-summary-breakdown">
                                        <div className="recon-summary-row matched">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span className="recon-summary-row-label">Auto-Matched</span>
                                            <span className="recon-summary-row-count">{summary.matched}</span>
                                        </div>
                                        <div className="recon-summary-row manual">
                                            <FontAwesomeIcon icon={faHandPointer} />
                                            <span className="recon-summary-row-label">Manually Matched</span>
                                            <span className="recon-summary-row-count">{summary.manually_matched}</span>
                                        </div>
                                        <div className="recon-summary-row suggestions">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            <span className="recon-summary-row-label">With Suggestions</span>
                                            <span className="recon-summary-row-count">{summary.with_suggestions}</span>
                                        </div>
                                        <div className="recon-summary-row unmatched">
                                            <FontAwesomeIcon icon={faBan} />
                                            <span className="recon-summary-row-label">No Match Found</span>
                                            <span className="recon-summary-row-count">{summary.unmatched - summary.with_suggestions}</span>
                                        </div>
                                        <div className="recon-summary-row total">
                                            <FontAwesomeIcon icon={faClipboardList} />
                                            <span className="recon-summary-row-label">Total Lines</span>
                                            <span className="recon-summary-row-count">{totalLines}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {reconPercent < 100 && (
                                <div className="recon-action-card">
                                    <div className="recon-action-card-content">
                                        <div className="recon-action-text">
                                            <h3>Continue Matching</h3>
                                            <p>There are still {totalLines - matchedCount} unreconciled lines. Go back to Step 2 to continue reviewing and matching.</p>
                                        </div>
                                        <button className="recon-action-btn secondary" onClick={() => goToStep(2)}>
                                            <FontAwesomeIcon icon={faArrowLeft} /> Back to Review
                                        </button>
                                    </div>
                                </div>
                            )}

                            {reconPercent === 100 && (
                                <div className="recon-complete-banner">
                                    <FontAwesomeIcon icon={faCheckCircle} className="recon-complete-icon" />
                                    <div>
                                        <h3>Reconciliation Complete</h3>
                                        <p>All {totalLines} bank statement lines have been successfully reconciled.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="recon-nav-buttons">
                        {currentStep > 1 && (
                            <button className="recon-nav-btn secondary" onClick={() => goToStep(currentStep - 1)}>
                                <FontAwesomeIcon icon={faArrowLeft} /> Previous
                            </button>
                        )}
                        <div className="recon-nav-spacer" />
                        {currentStep < 3 && (
                            <button className="recon-nav-btn primary" onClick={() => goToStep(currentStep + 1)}>
                                Next <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        )}
                        {currentStep === 3 && (
                            <button
                                className="recon-nav-btn primary"
                                onClick={() => history.push('/invoicing/bank-reconciliation')}
                            >
                                <FontAwesomeIcon icon={faCheck} /> Done
                            </button>
                        )}
                    </div>
                </div>

                <Footer />
            </div>

            {showManualMatch && selectedLine && (
                <ManualMatchModal
                    statementId={statementId}
                    hmsEntityId={statement?.hms_entity || null}
                    hmsEntityName={statement?.hms_entity_name || ''}
                    line={selectedLine}
                    onClose={() => setShowManualMatch(false)}
                    onComplete={handleManualMatchComplete}
                />
            )}
        </Layout>
    );
};

export default ReconciliationScreen;
